import React from "react";
import { Accordion, Table } from "react-bootstrap";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  REMCHANNEL_COLORS,
  LEGEND_LABEL_OVERRIDES,
  formatLegendLabel,
  getHiddenSections,
  CHART_PALETTE,
} from "./reportTheme";

/**
 * Short-term incentive results: eligibility, plan types, performance
 * assessment, measurement criteria and modifiers.
 * Reads only reportData plus the shared theme.
 */
const StiResultsSection = ({ reportData }) => {
  const hiddenSections = getHiddenSections();

  return (
  <Accordion.Item eventKey="2">
    <Accordion.Header className="border-bottom shadow-sm bg-white sticky-top">
    SHORT-TERM INCENTIVES ( STI) RESULTS
    </Accordion.Header>
    <Accordion.Body>
      <h5>STI eligibility</h5>
      <p>
      Participants reported eligibility for the company’s STI schemes based on employee grade. They specified whether all, some, or none of the employees in each grade were eligible to participate in the scheme and provided the potential values possible at different performance levels across various percentiles per the table below.  
      </p>
      {reportData.chartDataByGradingSystem &&
        reportData.chartDataByGradingSystem.stiEligibility && (
          <div style={{ height: "500px", marginBottom: "2rem", width: "100%" }}>
            <Bar
              data={{
                ...reportData.chartDataByGradingSystem.stiEligibility,
                datasets:
                  reportData.chartDataByGradingSystem.stiEligibility.datasets.map(
                    (dataset, index) => ({
                      ...dataset,
                      backgroundColor:
                        CHART_PALETTE[
                          index % CHART_PALETTE.length
                        ],
                      borderColor:
                        CHART_PALETTE[
                          index % CHART_PALETTE.length
                        ],
                      borderWidth: 1,
                    })
                  ),
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: "y",
                scales: {
                  x: {
                    stacked: true,
                    max: 100,
                    title: {
                      display: true,
                      text: "Percentage of Participants",
                    },
                  },
                  y: {
                    stacked: true,
                    title: {
                      display: true,
                      text: "Grades",
                    },
                  },
                },
                plugins: {
                  title: {
                    display: true,
                    text: "Eligibility to participate in Short-term Incentive Schemes",
                    font: {
                      size: 16,
                    },
                  },
                  legend: {
                    position: "bottom",
                    labels: {
                      generateLabels: (chart) => {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => ({
                          text: formatLegendLabel(dataset.label ?? ""),
                          fillStyle: dataset.backgroundColor,
                          strokeStyle: dataset.borderColor,
                          lineWidth: dataset.borderWidth ?? 1,
                          hidden: !chart.isDatasetVisible(i),
                          index: i,
                        }));
                      },
                    },
                  },
                  datalabels: {
                    formatter: (value) => {
                      return value > 0 ? value + "%" : "";
                    },
                    color: "#fff",
                    font: {
                      weight: "bold",
                    },
                  },
                },
              }}
            />
          </div>
        )}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />
 
      {(() => {
        // Find all grades where "None" is NOT 100% in the STI eligibility chart,
        // but are missing from all STI datasets due to insufficient data
        const stiEligibility = reportData.chartDataByGradingSystem.stiEligibility;
        const stiThreshold = reportData.chartDataByGradingSystem.stiThresholdAnnualReward;
        const stiTarget = reportData.chartDataByGradingSystem.stiTargetAnnualReward;
        const stiStretch = reportData.chartDataByGradingSystem.stiStretchAnnualReward;

        if (
          stiEligibility &&
          Array.isArray(stiEligibility.labels) &&
          Array.isArray(stiEligibility.datasets)
        ) {
          // Find the dataset index for "None"
          const noneIdx = stiEligibility.datasets.findIndex(
            ds => ds.label && ds.label.toLowerCase().includes("none")
          );
          let gradesWithNoneNot100 = [];
          if (noneIdx !== -1) {
            gradesWithNoneNot100 = stiEligibility.labels.filter((grade, i) => {
              const val = stiEligibility.datasets[noneIdx].data[i];
              return Number(val) !== 100;
            });
          }

          // Collect all grades present in any STI dataset
          const stiGrades = new Set([
            ...(stiThreshold?.labels || []),
            ...(stiTarget?.labels || []),
            ...(stiStretch?.labels || []),
          ]);

          // Filter gradesWithNoneNot100 to those NOT present in any STI dataset
          const missingGrades = gradesWithNoneNot100.filter(
            grade => !stiGrades.has(grade)
          );

          if (missingGrades.length > 0 && (!hiddenSections.stiParticipationBaseSalary || !hiddenSections.stiParticipationTGP)) {
            return (
              <div className="alert alert-warning my-2" style={{ fontSize: "0.95em" }}>
                <strong>Note:</strong> The following grades have STI eligibility but are not shown in the STI tables below due to insufficient STI data:{" "}
                <span style={{ fontWeight: 700 }}>
                  {missingGrades.join(", ")}
                </span>
              </div>
            );
          }
        }
        return null;
      })()}

      {(() => {
        // Defensive: check data exists
        if (!reportData?.chartDataByGradingSystem) {
          return null;
        }
        
        const threshold =
          reportData.chartDataByGradingSystem.stiThresholdAnnualReward;
        const target =
          reportData.chartDataByGradingSystem.stiTargetAnnualReward;
        const stretch =
          reportData.chartDataByGradingSystem.stiStretchAnnualReward;

        // Get all grades from the labels array (shared by all datasets)
        const grades = threshold?.labels || [];

        // Table rows for percentiles and their corresponding dataset labels
        const percentiles = [
          {
            label: (
              <>
                25<sup>th</sup>
              </>
            ),
            base: "Base Salary P25",
            tgp: "TGP P25",
          },
          {
            label: (
              <>
                50<sup>th</sup>
              </>
            ),
            base: "Base Salary P50",
            tgp: "TGP P50",
          },
          {
            label: (
              <>
                75<sup>th</sup>
              </>
            ),
            base: "Base Salary P75",
            tgp: "TGP P75",
          },
          {
            label: "Average",
            base: "Base Salary Average",
            tgp: "TGP Average",
          },
        ];

        // Helper to get value from dataset by label and grade index
        function getValue(ds, label, gradeIndex) {
          const dataset = ds?.datasets?.find((d) => d.label === label);
          if (!dataset || gradeIndex === -1) return null;
          return dataset.data[gradeIndex];
        }

        // Helper to format as currency
        function formatR(val) {
          if (val == null || isNaN(val)) return "";
          return (
            "R " +
            Number(val).toLocaleString("en-ZA", {
              maximumFractionDigits: 0,
            })
          );
        }

        // Helper to build grade indices map
        function buildGradeIndicesMap(grades) {
          return grades.reduce((acc, grade, idx) => {
            if (!acc[grade]) acc[grade] = [];
            acc[grade].push(idx);
            return acc;
          }, {});
        }

        // Helper to calculate average value
        function avgValue(ds, label, indices) {
          const values = indices
            .map((gIdx) => getValue(ds, label, gIdx))
            .filter((v) => v != null && !isNaN(v));
          if (!values.length) return null;
          return (
            values.reduce((sum, v) => sum + Number(v), 0) /
            values.length
          );
        }

        const gradeIndicesMap = buildGradeIndicesMap(grades);
        const uniqueGradeNames = Object.keys(gradeIndicesMap);

        return (
          <>
            {!hiddenSections.stiParticipationBaseSalary && (
              <>
                <h5>STI participation based on Base salary </h5>
                {/* Base Salary Table */}
                <Table striped bordered hover responsive className="mt-3">
                  <thead>
                    <tr>
                      <th style={{ width: "120px" }}>Grade</th>
                      <th>Percentile</th>
                      <th>Threshold STI values (Base salary)</th>
                      <th>Target STI values (Base salary)</th>
                      <th>Stretch Target STI values (Base salary)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueGradeNames.map((grade) =>
                      percentiles.map((p, pIdx) => {
                        const indices = gradeIndicesMap[grade];
                        return (
                          <tr key={grade + "-base-" + pIdx}>
                            {pIdx === 0 && (
                              <td rowSpan={percentiles.length}>{grade}</td>
                            )}
                            <td>{p.label}</td>
                            <td>{formatR(avgValue(threshold, p.base, indices))}</td>
                            <td>{formatR(avgValue(target, p.base, indices))}</td>
                            <td>{formatR(avgValue(stretch, p.base, indices))}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </>
            )}

            {!hiddenSections.stiParticipationTGP && (
              <>
                <h5>STI participation based on Total guaranteed package (TGP)</h5>
                {/* TGP Table */}
                <Table striped bordered hover responsive className="mt-3">
                  <thead>
                    <tr>
                      <th style={{ width: "120px" }}>Grade</th>
                      <th>Percentile</th>
                      <th>Threshold STI values (TGP)</th>
                      <th>Target STI values (TGP)</th>
                      <th>Stretch STI values (TGP)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueGradeNames.map((grade) =>
                      percentiles.map((p, pIdx) => {
                        const indices = gradeIndicesMap[grade];
                        return (
                          <tr key={grade + "-tgp-" + pIdx}>
                            {pIdx === 0 && (
                              <td rowSpan={percentiles.length}>{grade}</td>
                            )}
                            <td>{p.label}</td>
                            <td>{formatR(avgValue(threshold, p.tgp, indices))}</td>
                            <td>{formatR(avgValue(target, p.tgp, indices))}</td>
                            <td>{formatR(avgValue(stretch, p.tgp, indices))}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </>
        );
      })()}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {!hiddenSections.stiAllocationPercentagesByRemunerationBase && (
        <>
      {/* STI Annual Awards */}
      <h5>STI allocation percentages by remuneration base</h5>
      <p>
      The table below shows the average allocation percentages per grade for STI. Participants who entered monetary values were considered as a percentage of TGP basis to be included in these percentages. All values are rounded up to the nearest whole percentage.
      </p>
      <Table striped bordered hover responsive className="mt-3">
        <thead>
          <tr>
            <th>STI Remuneration Base</th>
            <th>Percentage</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            // Try to get baseTypeDistribution from reportData, fallback to empty object if not present
            const stiBaseTypeDistribution =
              reportData?.chartDataByGradingSystem?.baseTypeDistribution
                ?.sti || {};

            // Map for display names
            const baseTypeLabels = {
              percentage_base: "Percentage of Base Pay",
              percentage_tgp: "Percentage of Total Guaranteed Package",
              monetary_value: "Monetary Value by Grade",
              other: "Other",
            };

            // If no data, show "No data available"
            const keys = Object.keys(stiBaseTypeDistribution);
            if (!keys.length) {
              return (
                <tr>
                  <td colSpan="2" className="text-center">
                    No data available
                  </td>
                </tr>
              );
            }

            // Show all base types (even if 0%)
            // Sort keys alphabetically by base type label (or key if label not found)
            return keys
              .sort((a, b) => {
                const labelA = baseTypeLabels[a] || a;
                const labelB = baseTypeLabels[b] || b;
                return labelA.localeCompare(labelB);
              })
              .map((key) => (
                <tr key={key}>
                  <td>{baseTypeLabels[key] || key}</td>
                  <td>{Math.ceil(stiBaseTypeDistribution[key])}%</td>
                </tr>
              ));
          })()}
        </tbody>
      </Table>
        </>
      )}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {!hiddenSections.stiAllocationPercentagesByGrade && (
        <>
      <h5>STI allocation percentages by grade</h5>
      <p>
        The table below shows the average allocation percentages per grade for STI. Participants who entered monetary values were considered as a percentage of TGP basis to be included in these percentages. All values are rounded up to the nearest whole percentage.
      </p>
      {(() => {
        const stiBaseTypeAvg =
          reportData?.chartDataByGradingSystem?.baseTypeAverageOfPercentages?.sti;

        if (
          !stiBaseTypeAvg ||
          !Array.isArray(stiBaseTypeAvg.labels) ||
          !Array.isArray(stiBaseTypeAvg.datasets) ||
          stiBaseTypeAvg.datasets.length === 0
        ) {
          return (
            <Table striped bordered hover responsive className="mt-3 mb-4">
              <thead>
                <tr>
                  <th>Grade</th>
                  <th>Threshold</th>
                  <th>Target</th>
                  <th>Stretch</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="4" className="text-center">
                    No data available
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        }

        // Map dataset label to its data array for easy lookup
        const datasetMap = {};
        stiBaseTypeAvg.datasets.forEach((ds) => {
          datasetMap[ds.label] = ds.data;
        });

        // Get all unique dataset labels in the order: Threshold, Target, Stretch (if present)
        const allLabels = stiBaseTypeAvg.datasets.map((ds) => ds.label);
        const orderedLabels = ["Threshold", "Target", "Stretch"].filter((lbl) =>
          allLabels.includes(lbl)
        );

        return (
          <Table striped bordered hover responsive className="mt-3 mb-4">
            <thead>
              <tr>
                <th>Grade</th>
                {orderedLabels.map((lbl) => (
                  <th key={lbl}>{lbl}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stiBaseTypeAvg.labels.map((grade, idx) => (
                <tr key={grade}>
                  <td>{grade}</td>
                  {orderedLabels.map((lbl) => {
                    const value = datasetMap[lbl]?.[idx];
                    // Show as integer, rounded up, or "-" if not present
                    return (
                      <td key={lbl}>
                        {typeof value === "number" && !isNaN(value)
                          ? Math.ceil(value) + "%"
                          : "-"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        );
      })()}
        </>
      )}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      <h5>STI plan types by grade</h5>
      <p>
        The chart below shows the distribution of STI plan types by grade. Each bar represents the percentage of participants for each plan type within a grade.
      </p>
      {reportData.chartDataByGradingSystem?.stiPlanTypesPerformanceConditions && (
        <div style={{ height: "500px", marginBottom: "2rem" }}>
          <Bar
            data={{
              ...reportData.chartDataByGradingSystem.stiPlanTypesPerformanceConditions,
              datasets: reportData.chartDataByGradingSystem.stiPlanTypesPerformanceConditions.datasets.map(
                (dataset, index) => ({
                  ...dataset,
                  backgroundColor:
                    CHART_PALETTE
                      ? CHART_PALETTE[index % CHART_PALETTE.length]
                      : undefined,
                  borderColor:
                    CHART_PALETTE
                      ? CHART_PALETTE[index % CHART_PALETTE.length]
                      : undefined,
                  borderWidth: 1,
                })
              ),
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              indexAxis: "x",
              scales: {
                x: {
                  title: {
                    display: true,
                    text: "Grades",
                  },
                  stacked: false,
                },
                y: {
                  title: {
                    display: true,
                    text: "Percentage of Participants",
                  },
                  beginAtZero: true,
                  max: 100,
                },
              },
              plugins: {
                legend: {
                  position: "bottom",
                },
                datalabels: {
                  formatter: (value) => (value > 0 ? value + "%" : ""),
                  color: "#fff",
                  font: {
                    weight: "bold",
                  },
                },
              },
            }}
          />
        </div>
      )}


      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {/* STI Performance Assessment */}
      <h5>STI performance measurement criteria</h5>
      <p>
      Short-Term Incentive (STI) outcomes usually depend on company, team, and individual performance. The chart below shows their relative impact.  
      </p>

      {reportData.chartDataByGradingSystem.stiPerformanceAssessment ? (
        <div style={{ height: "500px", margin: "2rem 0", width: "100%" }}>
          <Bar
            data={{
              ...reportData.chartDataByGradingSystem
                .stiPerformanceAssessment,
              datasets:
                reportData.chartDataByGradingSystem.stiPerformanceAssessment.datasets.map(
                  (dataset, index) => ({
                    ...dataset,
                    backgroundColor:
                      CHART_PALETTE[
                        index % CHART_PALETTE.length
                      ],
                    borderColor:
                      CHART_PALETTE[
                        index % CHART_PALETTE.length
                      ],
                    borderWidth: 1,
                  })
                ),
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: {
                  stacked: true,
                  title: {
                    display: true,
                    text: "Grade",
                  },
                },
                y: {
                  stacked: true,
                  title: {
                    display: true,
                    text: "Percentage (%)",
                  },
                  max: 100,
                },
              },
              plugins: {
                legend: {
                  position: "bottom",
                  labels: {
                    boxWidth: 12,
                    generateLabels: (chart) => {
                      const datasets = chart.data.datasets;
                      return datasets.map((dataset, i) => ({
                        text: formatLegendLabel(dataset.label || ""),
                        fillStyle: dataset.backgroundColor,
                        strokeStyle: dataset.borderColor,
                        lineWidth: dataset.borderWidth,
                        hidden: !chart.isDatasetVisible(i),
                        index: i,
                      }));
                    },
                  },
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      return `${formatLegendLabel(context.dataset.label)}: ${context.raw}%`;
                    },
                  },
                },
                datalabels: {
                  color: "#FFFFFF",
                  font: {
                    weight: "bold",
                    size: 9,
                  },
                  formatter: function (value) {
                    return value > 0 ? value + "%" : "";
                  },
                },
              },
            }}
          />
        </div>
      ) : (
        <div className="text-center my-4">
          No performance assessment data available
        </div>
      )}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {/* Short-term Incentives performance conditions */}
      <h5>STI performance conditions</h5>
      <p>
      The graph below displays the performance conditions typically linked to STI plans and the percentage of participants who reported using them. 
      </p>
      {reportData.chartDataByGradingSystem?.stiPerformanceMeasures ? (
        <div style={{ height: "500px", margin: "2rem 0", width: "100%" }}>
          <Bar
            data={{
              ...reportData.chartDataByGradingSystem.stiPerformanceMeasures,
              datasets:
                reportData.chartDataByGradingSystem.stiPerformanceMeasures.datasets.map(
                  (dataset, index) => ({
                    ...dataset,
                    backgroundColor: dataset.data.map(
                      (_, i) =>
                        CHART_PALETTE[
                          i % CHART_PALETTE.length
                        ]
                    ),
                    borderColor: dataset.data.map(
                      (_, i) =>
                        CHART_PALETTE[
                          i % CHART_PALETTE.length
                        ]
                    ),
                    borderWidth: 1,
                  })
                ),
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              indexAxis: "x",
              scales: {
                y: {
                  title: {
                    display: true,
                    text: "Percentage (%)",
                  },
                  max: 100,
                },
                x: {
                  title: {
                    display: true,
                    text: "Performance Measure",
                  },
                },
              },
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      return `${context.raw}%`;
                    },
                  },
                },
                datalabels: {
                  color: "#FFFFFF",
                  font: {
                    weight: "bold",
                    size: 9,
                  },
                  anchor: "end",
                  align: "start",
                  formatter: function (value) {
                    return value > 0 ? value + "%" : "";
                  },
                },
              },
            }}
          />
        </div>
      ) : (
        <div className="text-center my-4">
          No STI performance measures data available
        </div>
      )}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      <h5>Performance modifiers</h5>
      <p>
      The table below presents the STI metrics that participants reported using as modifiers.  
      </p>
      {reportData.chartDataByGradingSystem?.stiPerformanceModifiers ? (
        <div style={{ margin: "2rem 0" }}>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Performance level</th>
                <th>Performance modifier</th>
              </tr>
            </thead>
            <tbody>
              {reportData.chartDataByGradingSystem.stiPerformanceModifiers.labels.map(
                (label, index) => (
                  <tr key={`performance-modifier-${label}-${index}`}>
                    <td>{label}</td>
                    <td>
                      {
                        reportData.chartDataByGradingSystem
                          .stiPerformanceModifiers.datasets[0].data[index]
                      }
                      %
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center my-4">
          No STI performance modifiers data available
        </div>
      )}
    </Accordion.Body>
  </Accordion.Item>
  );
};

export default StiResultsSection;
